import type { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import type { CameraInterface } from "./Engine";
import type { CSS2DRenderer } from "./renderer/CSS2DRenderer";
import type { CSS3DRenderer } from "./renderer/CSS3DRenderer.js";
import type { IRenderer } from "./renderer/IRenderer";

type EngineResizeSize = {
  width: number;
  height: number;
};

export type EngineResizeControllerOptions = {
  renderer: IRenderer;
  cssRenderer: CSS2DRenderer;
  cssRenderer3d: CSS3DRenderer;
  getBindElement: () => HTMLElement | undefined;
  getCamera: () => CameraInterface | undefined;
  getComposer: () => EffectComposer | undefined;
  getSize: () => EngineResizeSize;
  setSize: (width: number, height: number) => void;
  setCssRendererAdded: (value: boolean) => void;
  setCssRenderer3dAdded: (value: boolean) => void;
  requestUpdate: () => void;
  dispatchResize: () => void;
};

export class EngineResizeController {
  private _resizeObserver?: ResizeObserver;
  private _observedElement?: HTMLElement;
  private _resizeAnimationFrame = 0;
  private _resizeInitialized = false;
  private _isResizing = false;

  constructor(private readonly _options: EngineResizeControllerOptions) {}

  bind(element: HTMLElement | undefined): void {
    if (this._observedElement) {
      this._resizeObserver?.unobserve(this._observedElement);
      this._observedElement = undefined;
    }

    this.resize();

    if (!this._resizeObserver) {
      this._resizeObserver = new ResizeObserver((entries) => {
        const cr = entries[0]?.contentRect;
        if (cr && cr.width > 0 && cr.height > 0) {
          this._scheduleObservedResize(cr.width, cr.height);
        }
      });
    }

    if (element) {
      this._resizeObserver.observe(element);
      this._observedElement = element;
    }

    this.prepareRendererDomElements();
    this.appendRendererDomElements();
  }

  resize(force = true): void {
    if (this._isResizing) return;
    this._isResizing = true;

    try {
      const { width, height } = this._readResizeSize();
      const current = this._options.getSize();
      const sizeChanged =
        !this._resizeInitialized ||
        width !== current.width ||
        height !== current.height;

      if (!force && !sizeChanged) {
        return;
      }

      this._options.setSize(width, height);
      this._resizeInitialized = true;

      const renderer = this._options.renderer.instance;
      renderer.domElement.width = width;
      renderer.domElement.height = height;

      this._options.cssRenderer.setSize(width, height);
      this._options.cssRenderer3d.setSize(width, height);
      renderer.setSize(width, height, false);
      renderer.setDrawingBufferSize(width, height, 2);
      this._options.getCamera()?.update(width, height);

      this._ensureRendererDomAttached();

      const composer = this._options.getComposer();
      if (composer) {
        const pixelRatio = renderer.getPixelRatio();
        composer.setSize(width, height);
        composer.setPixelRatio(pixelRatio);
      }

      if (sizeChanged) {
        this._options.dispatchResize();
      }

      this._options.requestUpdate();
    } finally {
      this._isResizing = false;
    }
  }

  prepareRendererDomElements(): void {
    const host = this._options.getBindElement();
    if (host) {
      if (window.getComputedStyle(host).position === "static") {
        host.style.position = "relative";
      }
      host.style.overflow = "hidden";
      host.style.minWidth = "0";
      host.style.minHeight = "0";
    }

    const canvas = this._options.renderer.instance.domElement;
    canvas.style.display = "block";
    canvas.style.position = "absolute";
    canvas.style.top = "0px";
    canvas.style.left = "0px";
    canvas.style.width = "100%";
    canvas.style.height = "100%";

    [
      this._options.cssRenderer.domElement,
      this._options.cssRenderer3d.domElement,
    ].forEach((element) => {
      element.classList.add("css-renderer");
      element.style.position = "absolute";
      element.style.top = "0px";
      element.style.left = "0px";
      element.style.pointerEvents = "none";
    });
  }

  appendRendererDomElements(): void {
    const host = this._options.getBindElement();
    if (!host) return;

    host.appendChild(this._options.renderer.instance.domElement);
    host.appendChild(this._options.cssRenderer.domElement);
    this._options.setCssRendererAdded(true);
    host.appendChild(this._options.cssRenderer3d.domElement);
    this._options.setCssRenderer3dAdded(true);
  }

  private _normalizeResizeDimension(value: number, fallback = 128): number {
    const size = Number.isFinite(value) && value > 0 ? value : fallback;
    return Math.max(1, Math.floor(size));
  }

  private _readResizeSize(): EngineResizeSize {
    const element =
      this._options.getBindElement() ??
      this._options.renderer.instance.domElement;
    const { width, height } = element.getBoundingClientRect();

    return {
      width: this._normalizeResizeDimension(width),
      height: this._normalizeResizeDimension(height),
    };
  }

  private _ensureRendererDomAttached(): void {
    const host = this._options.getBindElement();
    if (!host) return;

    this.prepareRendererDomElements();

    const canvas = this._options.renderer.instance.domElement;
    if (canvas.parentElement !== host) {
      host.appendChild(canvas);
    }
  }

  private _scheduleObservedResize(width: number, height: number): void {
    const nextWidth = this._normalizeResizeDimension(width);
    const nextHeight = this._normalizeResizeDimension(height);
    const current = this._options.getSize();

    if (
      this._resizeInitialized &&
      nextWidth === current.width &&
      nextHeight === current.height
    ) {
      return;
    }

    if (this._resizeAnimationFrame) return;

    this._resizeAnimationFrame = requestAnimationFrame(() => {
      this._resizeAnimationFrame = 0;
      this.resize(false);
    });
  }
}
